/**
 * AutoLISP S-expression parser — token stream → AST. Supports quoted
 * expressions, dotted pairs inside lists, and honest unbalanced-paren errors.
 */

import { LispError } from './errors';
import { tokenize, type Token } from './lexer';

export type LispNode =
    | { t: 'num'; v: number; isInt: boolean }
    | { t: 'str'; v: string }
    | { t: 'sym'; name: string }
    | { t: 'cons'; items: LispNode[]; dot?: LispNode | null }
    | { t: 'quote'; node: LispNode };

class TokenStream {
    private pos = 0;
    constructor(private readonly tokens: Token[]) {}

    peek(): Token | null {
        return this.tokens[this.pos] ?? null;
    }

    next(): Token | null {
        return this.tokens[this.pos++] ?? null;
    }
}

function parseNode(stream: TokenStream): LispNode {
    const tok = stream.next();
    if (!tok) throw new LispError('malformed expression');
    switch (tok.t) {
        case 'num':
            return { t: 'num', v: tok.v, isInt: tok.isInt };
        case 'str':
            return { t: 'str', v: tok.v };
        case 'sym':
            return { t: 'sym', name: tok.v };
        case 'quote': {
            const inner = parseNode(stream);
            return { t: 'quote', node: inner };
        }
        case 'open': {
            const items: LispNode[] = [];
            let dot: LispNode | null = null;
            for (;;) {
                const peeked = stream.peek();
                if (!peeked) throw new LispError('malformed list');
                if (peeked.t === 'close') {
                    stream.next();
                    break;
                }
                if (peeked.t === 'dot') {
                    stream.next();
                    dot = parseNode(stream);
                    const closing = stream.next();
                    if (!closing || closing.t !== 'close') throw new LispError('malformed dotted pair');
                    break;
                }
                items.push(parseNode(stream));
            }
            return dot ? { t: 'cons', items, dot } : { t: 'cons', items };
        }
        case 'close':
            throw new LispError('extra right paren');
        case 'dot':
            throw new LispError('misplaced dot');
    }
}

/** Parse a whole source file into top-level forms. */
export function parseProgram(src: string): LispNode[] {
    const stream = new TokenStream(tokenize(src));
    const forms: LispNode[] = [];
    while (stream.peek()) forms.push(parseNode(stream));
    return forms;
}
